import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Barcode } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useStore } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate } from "@/lib/status";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = [
  { key: "", label: "Tutte" },
  { key: "waiting", label: "In attesa" },
  { key: "working", label: "In lavorazione" },
  { key: "ready", label: "Pronte" },
  { key: "delivered", label: "Consegnate" },
];

export default function RepairsList() {
  const { selectedStore } = useStore();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [bucket, setBucket] = useState("");
  const [rows, setRows] = useState([]);
  const [scan, setScan] = useState("");
  const [staff, setStaff] = useState([]);
  const [assigned, setAssigned] = useState("all");

  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const scanBarcode = async (code) => {
    const val = (code || "").trim();
    if (!val) return;
    try {
      const { data } = await api.get(`/repairs/by-barcode/${encodeURIComponent(val)}`);
      setScan("");
      navigate(`/repairs/${data.id}`);
    } catch {
      toast.error("Nessuna riparazione trovata per questo codice");
    }
  };

  useEffect(() => {
    api.get("/repairs", { params: { store_id: selectedStore, bucket: bucket || undefined, q: q || undefined, assigned: assigned === "all" ? undefined : assigned } })
      .then((r) => setRows(r.data));
  }, [selectedStore, bucket, q, assigned]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">Riparazioni</h1>
          {q && <p className="text-sm text-muted-foreground">Risultati per “{q}”</p>}
        </div>
        <Button data-testid="repairs-new" onClick={() => navigate("/repairs/new")}>Nuova riparazione</Button>
      </div>

      <div className="relative max-w-sm">
        <Barcode className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="barcode-search" className="pl-9" value={scan}
          onChange={(e) => setScan(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") scanBarcode(scan); }}
          placeholder="Scansiona o inserisci codice a barre / n° pratica…" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`repairs-tab-${t.key || "all"}`}
            onClick={() => setBucket(t.key)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              bucket === t.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
            }`}>
            {t.label}
          </button>
        ))}
        <div className="ml-auto">
          <Select value={assigned} onValueChange={setAssigned}>
            <SelectTrigger className="w-56" data-testid="filter-technician"><SelectValue placeholder="Tutti i tecnici" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tecnici</SelectItem>
              <SelectItem value="none">Non assegnate</SelectItem>
              {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">N° pratica</th>
                <th className="px-4 py-3">Dispositivo</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3 hidden lg:table-cell">Tecnico</th>
                <th className="px-4 py-3 hidden md:table-cell">Negozio</th>
                <th className="px-4 py-3">Stato</th>
                <th className="px-4 py-3 hidden lg:table-cell">Ricevuto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`repair-row-${r.repair_number}`}
                  onClick={() => navigate(`/repairs/${r.id}`)}
                  className="cursor-pointer transition-colors hover:bg-secondary">
                  <td className="px-4 py-3 font-mono font-semibold text-primary">{r.repair_number}</td>
                  <td className="px-4 py-3">{r.device.brand} {r.device.model}</td>
                  <td className="px-4 py-3">{r.customer_name}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{r.assigned_user_name || "—"}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{r.store_name}</td>
                  <td className="px-4 py-3"><StatusBadge code={r.status_code} /></td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{fmtDate(r.received_at)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">Nessuna riparazione trovata.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
